import {Fragment, useCallback, useEffect, useState} from 'react';
import {Container} from '@/components/container';
import {CandidateContent} from './';
import {Navbar, NavbarActions} from "@/partials/navbar";
import {NavbarDropdown} from './menu/NavbarDropdown';
import {useParams} from 'react-router-dom';
import axios from 'axios';
import {KeenIcon} from "@/components";
import {CandidateProfileHero} from "@/pages/buddy/candidate/hero/CandidatePofileHero";
import {PageMenu} from './menu/PageMenu';
import {CandidateDetailsData} from "@/pages/buddy/types/CandidateDetailsData";
import {CandidateModal} from "@/pages/buddy/candidate/blocks/source/CandidateModal";
import {Loading} from '@/layouts/buddy/loading';
import {enqueueSnackbar} from "notistack";

const CandidatePage = () => {
    const [candidate, setCandidate] = useState<CandidateDetailsData | null>(null);
    const [loading, setLoading] = useState(true);
    const {tab, p_id} = useParams<{ tab?: string; p_id: string }>();
    const [activePage, setActivePage] = useState<string>(tab || 'overview');
    const [noActiveMatches, setNoActiveMatches] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [candidateModalOpen, setCandidateModalOpen] = useState(false);

    const handleOpen = () => setCandidateModalOpen(true);
    const handleClose = () => {
        setCandidateModalOpen(false);
        setRefreshKey(prev => prev + 1);
    };

    const API_CANDIDATE = `${import.meta.env.VITE_APP_API_URL}/candidate`;
    const API_MATCH = `${import.meta.env.VITE_APP_API_URL}/match`;
    const [loadingJobHistory, setLoadingJobHistory] = useState<boolean>(false);
    const [loadingMatches, setLoadingMatches] = useState<boolean>(false);

    async function processJobHistory(candidate_p_id: string): Promise<any[]> {
        const response = await axios.post<CandidateDetailsData>(
            `${API_CANDIDATE}/process/job-history/${candidate_p_id}`
        );
        return response.data.job_history;
    }

    async function processMatches(candidate_p_id: string): Promise<any[] | null> {
        const response = await axios.post(
            `${API_MATCH}/process/candidate/${candidate_p_id}`,
            {},
            { validateStatus: () => true }
        );

        if (response.status === 204) {
            return null;
        }

        return response.data;
    }

    const triggerRefresh = () => setRefreshKey(prev => prev + 1);

    const fetchCandidateData = async () => {
        setLoading(true);
        try {
            const response = await axios.get<CandidateDetailsData>(`${API_CANDIDATE}/${p_id}`, {
                headers: {Accept: 'application/json'},
            });
            const fetchedCandidate = response.data;
            setCandidate(fetchedCandidate);
            setLoading(false);

            if (!fetchedCandidate.job_history_processed_at) {
                setLoadingJobHistory(true);
                setLoadingMatches(true);
                try {
                    const returnedJobHistory = await processJobHistory(fetchedCandidate.p_id);
                    setCandidate(prevCandidate =>
                        prevCandidate
                            ? {
                                ...prevCandidate,
                                job_history: returnedJobHistory,
                                job_history_processed_at: new Date().toISOString(),
                            }
                            : null
                    );
                } catch (err) {
                    enqueueSnackbar(`Error processing job history: ${err}`, {variant: 'error'});
                    setCandidate(prevCandidate =>
                        prevCandidate ? {...prevCandidate, job_history: []} : null
                    );
                } finally {
                    setLoadingJobHistory(false);
                }

                try {
                    const returnedMatches = await processMatches(fetchedCandidate.p_id);
                    if (returnedMatches) {
                        enqueueSnackbar(`Match was successfully processed.`, {variant: 'info'});
                    }
                } catch (err) {
                    enqueueSnackbar(`Error processing matches: ${err}`, {variant: 'error'});
                } finally {
                    setLoadingMatches(false);
                }
            } else {
                setLoadingMatches(true);
                try {
                    const returnedMatches = await processMatches(fetchedCandidate.p_id);
                    if (returnedMatches) {
                        enqueueSnackbar(`Matches were successfully processed.`, {variant: 'info'});
                    }
                } catch (err) {
                    enqueueSnackbar(`Error processing matches: ${err}`, {variant: 'error'});
                } finally {
                    setLoadingMatches(false);
                }
            }
        } catch (err) {
            enqueueSnackbar(`Failed to fetch candidate data: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidateData();
    }, [p_id, refreshKey]);

    const handleSummaryFetched = useCallback((activeMatches: number) => {
        setNoActiveMatches(activeMatches === 0);
    }, []);

    if (loading) return <Loading loading={loading}/>;
    if (!candidate) return <div>No candidate data available</div>;

    return (
        <Fragment>
            <CandidateProfileHero candidate={candidate}/>
            <CandidateModal
                mode="addSource"
                open={candidateModalOpen}
                onClose={handleClose}
                candidate={candidate}
                refreshCandidateData={handleClose}
            />

            <Container>
                <Navbar>
                    <PageMenu setActivePage={setActivePage}/>
                    <NavbarActions>
                        <button onClick={handleOpen} type="button" className="btn btn-sm btn-primary">
                            <KeenIcon icon="plus"/> Add New Source
                        </button>
                        {/*<NavbarDropdown/>*/}
                        </NavbarActions>
                </Navbar>
            </Container>

            <Container>
                <CandidateContent
                    activePage={activePage}
                    candidate={candidate}
                    loading={loading}
                    setActivePage={setActivePage}
                    handleSummaryFetched={handleSummaryFetched}
                    loadingJobHistory={loadingJobHistory}
                    loadingMatches={loadingMatches}
                    onCpsUpdateComplete={triggerRefresh}
                />
            </Container>
        </Fragment>
    );
};

export {CandidatePage};
